/**
 * 飞书错误上报工具 - H5 React版本
 *
 * 功能说明：
 * 1. 监听全局JavaScript错误（window.onerror）
 * 2. 监听未捕获的Promise rejection（unhandledrejection）
 * 3. 监听资源加载错误（可选）
 * 4. 错误去重和队列管理
 * 5. 批量上报到飞书webhook
 * 6. 支持采样率控制
 *
 * 使用方式：
 * ```typescript
 * import { initFeishuErrorReporter, destroyFeishuErrorReporter } from '@/utils/feishu'
 *
 * // 初始化（只在生产环境启用）
 * if (import.meta.env.VITE_NODE_ENV === 'production') {
 *   initFeishuErrorReporter({
 *     webhookUrls: {
 *       production: import.meta.env.VITE_FEISHU_WEBHOOK_URL || '',
 *     },
 *     environment: 'production',
 *     getUserInfo: () => ({
 *       userId: '123',
 *       username: 'test',
 *       unionId: 'union123'
 *     }),
 *     sampleRate: 1.0, // 采样率 0-1，默认 1.0 全量上报
 *     captureResourceError: true, // 是否捕获资源加载错误，默认 true
 *   })
 * }
 *
 * // 销毁（SPA 应用卸载时调用）
 * destroyFeishuErrorReporter()
 * ```
 */

import type { ErrorInfo, FeishuErrorReporterConfig } from './types';
import {
  setupJSErrorListener,
  // setupPromiseRejectionListener,
  // setupResourceErrorListener,
  removeAllListeners,
} from './listeners';
import { addErrorToQueue, clearErrorQueue, getErrorQueueStatus, setQueueConfig } from './queue';
import { isEnabled } from './reporter';

// 导出类型
export type { ErrorInfo, UserInfo, FeishuErrorReporterConfig } from './types';

// 导出格式化工具
export { formatErrorReason } from './formatters';

// 状态管理
let config: FeishuErrorReporterConfig | null = null;
let isInitialized = false;

/**
 * 初始化飞书错误上报器
 */
export function initFeishuErrorReporter(reporterConfig: FeishuErrorReporterConfig): void {
  // 防止重复初始化
  if (isInitialized) {
    console.warn('飞书错误上报器已初始化，请勿重复调用。如需重新初始化，请先调用 destroyFeishuErrorReporter()');
    return;
  }

  console.log('初始化飞书错误监听器...');

  // 保存配置
  config = reporterConfig;
  setQueueConfig(config);

  // 检查是否启用
  if (!isEnabled(config)) {
    console.log('错误上报未启用（当前环境:', config.environment, ')');
    return;
  }

  // 设置JavaScript错误监听
  setupJSErrorListener(config);

  // 设置Promise rejection监听
  // setupPromiseRejectionListener(config);

  // // 设置资源加载错误监听（可配置）
  // if (config.captureResourceError !== false) {
  //   setupResourceErrorListener(config);
  // }

  isInitialized = true;
  console.log('飞书错误监听器初始化完成');
}

/**
 * 手动上报错误
 */
export function reportCustomError(message: string, extra?: Partial<ErrorInfo>): void {
  if (!config) {
    console.warn('飞书错误上报器未初始化');
    return;
  }

  addErrorToQueue(
    {
      message,
      errorType: 'custom-error',
      ...extra,
    },
    config,
  );
}

/**
 * 获取错误队列状态（用于调试）
 */
export { getErrorQueueStatus };

/**
 * 清理错误队列（用于测试或重置）
 */
export { clearErrorQueue };

/**
 * 销毁飞书错误上报器（移除所有监听器）
 * 在 SPA 应用卸载时调用，避免内存泄漏
 */
export function destroyFeishuErrorReporter(): void {
  if (!isInitialized) {
    return;
  }

  // 移除所有监听器
  removeAllListeners();

  // 清理状态
  config = null;
  setQueueConfig(null);
  clearErrorQueue();
  isInitialized = false;

  console.log('飞书错误上报器已销毁');
}
